import React, { useState, useEffect } from 'react';
import { Star, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ReviewModal
 * Props:
 *  - open, onClose, orderId, token
 *  - onSubmitted (review) — optional
 */
const ReviewModal = ({ open, onClose, orderId, token, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!open || !orderId) return;
    setExisting(null);
    setRating(0); setComment('');
    axios.get(`${API}/reviews/order/${orderId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.data) {
          setExisting(r.data);
          setRating(r.data.rating);
          setComment(r.data.comment || '');
        }
      }).catch(() => {});
  }, [open, orderId, token]);

  const submit = async () => {
    if (rating < 1) return toast.error('Lütfen 1-5 arası puan verin');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reviews`, {
        order_id: orderId, rating, comment: comment.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Yorumunuz için teşekkürler!');
      onSubmitted?.(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Yorum gönderilemedi');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl">Siparişinizi Değerlendirin</DialogTitle>
        </DialogHeader>

        {existing ? (
          <div className="space-y-3 py-4">
            <p className="text-charcoal/60 text-sm">Bu sipariş için zaten yorum bıraktınız:</p>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(n => (
                <Star key={n} className={`w-6 h-6 ${n <= existing.rating ? 'fill-gold text-gold' : 'text-charcoal/20'}`} />
              ))}
            </div>
            {existing.comment && <p className="text-charcoal/80 italic bg-sand p-3 rounded-lg">"{existing.comment}"</p>}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm text-charcoal/60 mb-2">Deneyiminiz nasıldı?</p>
              <div className="flex items-center gap-2 justify-center" data-testid="review-stars">
                {[1,2,3,4,5].map(n => (
                  <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    className="p-1 transition-transform hover:scale-110 active:scale-95"
                    data-testid={`review-star-${n}`}
                  >
                    <Star className={`w-9 h-9 ${n <= (hover || rating) ? 'fill-gold text-gold' : 'text-charcoal/20'}`} />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center mt-2 text-sm font-medium text-terracotta">
                  {rating === 5 ? 'Mükemmel!' : rating === 4 ? 'Çok iyi' : rating === 3 ? 'Fena değil' : rating === 2 ? 'İdare eder' : 'Kötü'}
                </p>
              )}
            </div>
            <Textarea
              placeholder="Düşünceleriniz... (opsiyonel)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              className="min-h-[100px]"
              data-testid="review-comment"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1">Vazgeç</Button>
              <Button onClick={submit} disabled={loading || rating < 1}
                className="flex-1 bg-terracotta text-white hover:bg-terracotta/90"
                data-testid="review-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Gönder'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
